import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import BoozeInfoPanelList from "../../components/BoozeInfoPanels";
import BoozeList from "../../components/BoozeList";
import BoozeBuySuccessPopup from "../../components/BoozePopup/BoozeBuySuccessPopup";
import BoozeSellSuccessPopup from "../../components/BoozePopup/BoozeSellSuccessPopup";
import CrimeCaughtPopup from "../../components/CrimePopup/CrimeCaughtPopup";
import CrimeFailedPopup from "../../components/CrimePopup/CrimeFailedPopup";
import HelperBotWorkingPopup from "../../components/HelperBotPopup/HelperBotWorkingPopup";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { City } from "../../constants/const";
import { SmugglingActionType } from "../../constants/enum/enum";
import { RankXP } from "../../constants/rank";
import useCurrentTime from "../../hook/useCurrentTime";
import useGameBankBalance from "../../hook/useGameBankBalance";
import useWallet from "../../hook/useWallet";
import {
  finishSmuggle,
  getAmountLimitAction,
  getBoozeHoldingsAction,
  getBoozeTotalProfitAction,
  getBusinessProfileAction,
  getCityMarketPriceAction,
  getNextBoozeTimeAction,
  getSmugglingInfoAction,
  getSmugglingMarketLaunchBlockAction,
  getSmugglingMarketUpdateIntervalAction,
  getUserHelperBotInfo,
} from "../../reducers/game.slice";
import { getMyProfile } from "../../reducers/profile.slice";
import { convertDateTime } from "../../utils/utils";
import useBoozeSSE from "./hooks/useBoozeSSE";

import useStyles from "./index.styles";

const Booze = () => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const { account } = useWallet();
  const currentTime = useCurrentTime();

  const { myProfile } = useAppSelector((state) => state.profile);
  const {
    nextBoozeTime,
    boozeCountLimit,
    boozePrices,
    boozeHoldings,
    isLoadingBoozePrices,
    userBoozeBotInfo,
  } = useAppSelector((state) => state.game);
  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { updateBalance } = useGameBankBalance(account, signMsg, signature);

  const [openBotWorkingPopup, setOpenBotWorkingPopup] = useState(false);
  const [openBuySuccessPopup, setOpenBuySuccessPopup] = useState(false);
  const [openSellSuccessPopup, setOpenSellSuccessPopup] = useState(false);
  const [openFailedModal, setOpenFailedModal] = useState(false);
  const [openCaughtModal, setOpenCaughtModal] = useState(false);

  const [successCashAmount, setSuccessCashAmount] = useState(0);
  const [successXpPercent, setSuccessXpPercent] = useState(0);
  const [smugglingBoozeType, setSmugglingBoozeType] = useState("");
  const [smugglingBoozeCount, setSmugglingBoozeCount] = useState(0);

  const renderStatus = () => {
    if (currentTime > nextBoozeTime) {
      return "Now!!!";
    }

    const timeRemaining = convertDateTime(nextBoozeTime - currentTime);
    return timeRemaining;
  };

  // Memoize the callback functions for useCrimeSSE
  const onSuccess = useCallback(
    (actionType: SmugglingActionType, cashAmount: number, xpAmount: number) => {
      if (actionType === SmugglingActionType.Buy) {
        setOpenBuySuccessPopup(true);
      } else if (actionType === SmugglingActionType.Sell) {
        setOpenSellSuccessPopup(true);
      }
      setSuccessCashAmount(cashAmount);
      updateBalance(account, signMsg, signature);

      dispatch(getNextBoozeTimeAction(account));
      dispatch(finishSmuggle());
      dispatch(
        getSmugglingInfoAction({ categoryId: 0, cityId: myProfile.city || 0 })
      );
      if (myProfile.rankNumber && myProfile.rankNumber < RankXP.length - 1) {
        const xpPercent =
          (xpAmount /
            (RankXP[myProfile.rankNumber] - RankXP[myProfile.rankNumber - 1])) *
          100;

        setSuccessXpPercent(xpPercent);
      }
      if (myProfile.name) {
        dispatch(getMyProfile(myProfile.name));
      }
    },
    [
      account,
      dispatch,
      myProfile.name,
      myProfile.rankNumber,
      signMsg,
      signature,
      updateBalance,
      myProfile.city,
    ]
  );

  const onCaught = useCallback(() => {
    setOpenCaughtModal(true);
    dispatch(getNextBoozeTimeAction(account));
    dispatch(finishSmuggle());
  }, [account, dispatch]);

  const onFailed = useCallback(() => {
    setOpenFailedModal(true);
    dispatch(getNextBoozeTimeAction(account));
    dispatch(finishSmuggle());
  }, [account, dispatch]);

  useBoozeSSE(account, onSuccess, onCaught, onFailed, () => {});

  useEffect(() => {
    dispatch(getCityMarketPriceAction(myProfile.city || 0));
    dispatch(
      getBusinessProfileAction({ itemId: 45, cityId: myProfile.city || 0 })
    );
    dispatch(getBoozeTotalProfitAction({ city: myProfile.city || 0 }));
    dispatch(
      getSmugglingInfoAction({ categoryId: 0, cityId: myProfile.city || 0 })
    );
  }, [myProfile.city, dispatch]);

  useEffect(() => {
    if (!account) return;

    dispatch(getNextBoozeTimeAction(account));
    dispatch(getAmountLimitAction(account));
    dispatch(getBoozeHoldingsAction(account));
    dispatch(getUserHelperBotInfo(account));
  }, [account, dispatch]);

  useEffect(() => {
    dispatch(getSmugglingMarketLaunchBlockAction());
    dispatch(getSmugglingMarketUpdateIntervalAction());
  }, [dispatch]);

  useEffect(() => {
    if (userBoozeBotInfo.isRunning) {
      // setOpenBotWorkingPopup(true);
    }
  }, [userBoozeBotInfo]);

  return (
    <Box className={classes.body}>
      <Box className={classes.container}>
        <Box className={classes.header}>
          <Box className={classes.headerLeft}>
            <Box className={classes.title}>
              <Typography
                fontFamily={"Philosopher"}
                color={"white"}
                textAlign={"center"}
                fontSize={{ xs: 24, sm: 36 }}
                fontWeight={900}
              >
                Booze warehouse
              </Typography>

              <Box
                component={"img"}
                src={"/assets/imgs/booze/liquor.png"}
                alt="Booze Image"
                width={48}
                height={48}
              />
            </Box>

            <Typography
              fontFamily={"Philosopher"}
              color={"grey"}
              fontSize={14}
              fontWeight={900}
            >
              {City[myProfile.city ?? 0]}
            </Typography>
          </Box>

          <Box className={classes.headerRight}>
            <Typography
              fontFamily={"Philosopher"}
              color={"grey"}
              textAlign={"center"}
              fontSize={16}
              fontWeight={700}
            >
              Max {boozeCountLimit} units
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                fontFamily={"Philosopher"}
                color={"white"}
                textAlign={"center"}
                fontSize={{ xs: 12, sm: 16 }}
                fontWeight={700}
              >
                Your next buy/sell:{" "}
              </Typography>
              <Box
                component={"img"}
                src={"/assets/imgs/travel/clockwise.png"}
                alt="Clock"
              />

              <Typography
                fontFamily={"Philosopher"}
                color={"white"}
                textAlign={"center"}
                fontSize={16}
                fontWeight={700}
              >
                {renderStatus()}
              </Typography>
            </Box>
          </Box>
        </Box>

        <BoozeList
          setSmugglingBoozeType={(boozeType: string) => {
            setSmugglingBoozeType(boozeType);
          }}
          setSmugglingBoozeCount={(boozeCount: number) => {
            setSmugglingBoozeCount(boozeCount);
          }}
          boozeCountLimit={boozeCountLimit}
          boozePrices={boozePrices}
          boozeHoldings={boozeHoldings}
          isLoadingBoozePrices={isLoadingBoozePrices}
          disabled={currentTime < nextBoozeTime}
        />

        <BoozeInfoPanelList />
      </Box>

      <HelperBotWorkingPopup
        openModal={openBotWorkingPopup}
        setOpenModal={setOpenBotWorkingPopup}
      />

      <BoozeBuySuccessPopup
        smugglingBoozeType={smugglingBoozeType}
        smugglingBoozeCount={smugglingBoozeCount}
        successCashAmount={successCashAmount}
        successXpPercent={successXpPercent}
        openModal={openBuySuccessPopup}
        setOpenModal={setOpenBuySuccessPopup}
      />

      <BoozeSellSuccessPopup
        smugglingBoozeType={smugglingBoozeType}
        smugglingBoozeCount={smugglingBoozeCount}
        successCashAmount={successCashAmount}
        successXpPercent={successXpPercent}
        openModal={openSellSuccessPopup}
        setOpenModal={setOpenSellSuccessPopup}
      />

      <CrimeCaughtPopup
        openModal={openCaughtModal}
        setOpenModal={setOpenCaughtModal}
      />
      <CrimeFailedPopup
        openModal={openFailedModal}
        setOpenModal={setOpenFailedModal}
      />
    </Box>
  );
};

export default Booze;
